import { useParams, Link, Navigate } from 'react-router-dom';
import { Reveal } from '@/components/Reveal';
import { ProjectCard } from '@/components/ProjectCard';
import { Paragraphs } from '@/components/ui/paragraphs';
import { useServices } from '@/data/useServices';
import { useProjects } from '@/data/useProjects';

export default function CategoryPage() {
  const { serviceSlug } = useParams();
  const {
    services: SERVICES,
    getServiceBySlug,
    loading: servicesLoading,
    error: servicesError,
  } = useServices();
  const {
    getProjectsByCategory,
    loading: projectsLoading,
    error: projectsError,
  } = useProjects();
  const service = getServiceBySlug(serviceSlug);

  if (!service) {
    if (servicesLoading) {
      return (
        <div className="min-h-[65vh] bg-[#EFECE7] flex items-center justify-center">
          <div className="w-12 h-12 rounded-full border-4 border-[#58677A]/15 border-t-[#ED6B24] animate-spin" aria-label="Cargando servicio" />
        </div>
      );
    }

    if (servicesError) {
      return (
        <div className="min-h-[65vh] bg-[#EFECE7] flex items-center justify-center px-6 text-center text-[#58677A]">
          <p className="max-w-lg">No pudimos cargar los servicios administrados en este momento. Recarga la página en unos segundos.</p>
        </div>
      );
    }

    return <Navigate to="/servicios-proyectos" replace />;
  }

  const projects = getProjectsByCategory(serviceSlug);

  return (
    <div className="text-[#58677A]" data-testid={`category-page-${service.slug}`} style={{ backgroundColor: '#EFECE7', backgroundImage: "url('/assets/backgrounds/nosotros-bg.jpg')", backgroundRepeat: 'repeat-y', backgroundSize: '100% auto', backgroundPosition: 'top center' }}>
      {/* Top: ¿QUÉ NECESITAS? + centered category menu */}
      <section className="pt-16 pb-10">
        <div className="container-link">
          <Reveal>
            <h1
              className="text-center uppercase mb-10 lg:mb-12"
              style={{
                color: '#ED6B24',
                fontSize: 'clamp(22pt, 5.5vw, 30pt)',
                fontFamily: 'Gotham, sans-serif',
                fontWeight: 900,
                letterSpacing: '0.02em',
              }}
              data-testid="category-page-title"
            >
              ¿QUÉ NECESITAS?
            </h1>
          </Reveal>

          {/* Category menu — 4 buttons per row on desktop; incomplete rows auto-center */}
          <div
            className="flex flex-wrap justify-center gap-2.5 mb-12 lg:mb-14 max-w-5xl mx-auto"
            data-testid="services-category-menu"
          >
            {SERVICES.map((s) => {
              const active = s.slug === serviceSlug;
              return (
                <Link
                  key={s.slug}
                  to={`/servicios/${s.slug}`}
                  className={`text-center whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] leading-tight font-bold uppercase border-2 transition-all duration-200
                    basis-full sm:basis-[calc((100%-0.625rem)/2)] md:basis-[calc((100%-1.25rem)/3)] lg:basis-[calc((100%-1.875rem)/4)] ${
                    active
                      ? 'bg-[#ED6B24] text-white border-[#ED6B24]'
                      : 'bg-white text-[#ED6B24] border-[#ED6B24] hover:bg-[#ED6B24]/10'
                  }`}
                  data-testid={`services-category-item-${s.slug}`}
                >
                  {s.name}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Banner */}
      {(() => {
        const sizes = service.hubBannerSizes || { smallPct: 100, mainPct: 100 };
        const smallScale = (sizes.smallPct || 100) / 100;
        const mainScale = (sizes.mainPct || 100) / 100;
        return (
      <section className="pb-14">
        <div className="container-link">
          <div
            className="relative rounded-[28px] overflow-hidden border border-black/10"
            data-testid={`category-banner-${service.slug}`}
            style={{ '--small-scale': smallScale, '--main-scale': mainScale }}
          >
            <div
  className="absolute inset-0 bg-cover bg-right sm:bg-center"
  style={{
    backgroundImage: `url(${service.hubBanner || service.bgImage})`,
  }}
/>
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(110deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.20) 60%, rgba(0,0,0,0.10) 100%)',
              }}
            />
            <div
              className="relative px-6 sm:px-10 lg:px-14 py-5 sm:py-7 lg:py-9 flex items-center"
              style={{ minHeight: 'clamp(100px, 16vw, 195px)' }}
            >
              <div className="max-w-[70%] sm:max-w-[65%] lg:max-w-[60%] text-white">
                {service.hubBannerText?.top && (
                  <p
                    className="uppercase font-semibold tracking-[0.04em] text-white/95 mb-1 sm:mb-2 leading-tight"
                    style={{ fontSize: `calc(clamp(11px, calc(11px + 5 * ((100vw - 640px) / 640)), 16px) * var(--small-scale, 1))` }}
                    data-testid={`category-banner-top-${service.slug}`}
                  >
                    {service.hubBannerText.top}
                  </p>
                )}
                <h2
                  className="uppercase font-black leading-[0.95] text-white tracking-tight"
                  style={{ fontSize: `calc(clamp(30px, calc(30px + 42 * ((100vw - 640px) / 640)), 72px) * var(--main-scale, 1))` }}
                  data-testid={`category-banner-main-${service.slug}`}
                >
                  {service.hubBannerText?.main || service.name}
                </h2>
                {service.hubBannerText?.bottom && (
                  <p
                    className="uppercase font-semibold tracking-[0.04em] text-white/95 mt-1 sm:mt-2 leading-tight"
                    style={{ fontSize: `calc(clamp(11px, calc(11px + 5 * ((100vw - 640px) / 640)), 16px) * var(--small-scale, 1))` }}
                    data-testid={`category-banner-bottom-${service.slug}`}
                  >
                    {service.hubBannerText.bottom}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
        );
      })()}

      {/* Description */}
      <section className="pb-4 sm:pb-6">
        <div className="container-link">
          <Reveal>
            <Paragraphs
              text={service.description}
              className="text-base sm:text-lg lg:text-xl text-[#58677A]/85 leading-relaxed"
              spacingClass="mb-4 last:mb-0"
              testId="service-description"
            />
          </Reveal>
        </div>
      </section>

      {/* Projects grid */}
      <section className="section-pad !pt-0">
        <div className="container-link">
          <Reveal>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black uppercase mb-8 lg:mb-12">
              {service.portfolioTitle}
            </h3>
          </Reveal>

          {projectsLoading && projects.length === 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" aria-label="Cargando proyectos">
              {[0, 1, 2].map((item) => (
                <div key={item} className="aspect-[4/3] rounded-2xl bg-white/45 animate-pulse" />
              ))}
            </div>
          ) : projectsError && projects.length === 0 ? (
            <p className="text-[#58677A]/70">Los proyectos no están disponibles temporalmente.</p>
          ) : projects.length === 0 ? (
            <p className="text-[#58677A]/70">Próximamente publicaremos más trabajos en esta categoría.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {projects.map((p, idx) => (
                <Reveal key={p.slug} delay={idx * 0.05}>
                  <ProjectCard project={p} testId="category-project-card" />
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
